import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Room, UnitSystem, roomVolumeFt3, roomVolumeM3, toM3h, cfm, fmt, fmtRequiredCfm, fmtRequiredM3h, fmtArea, fmtCeiling, requiredM3h,
} from '@/lib/calculator';
import { matchRoom, Preferences, Setup, gasGuidance, GAS_SOURCE_LABELS } from '@/lib/matching';
import { ShareState } from '@/lib/share';
import { METRIC_LABEL, getSource, SOURCE_TYPE_LABEL, getOffer, getFilterSet } from '@/data/catalogue';

const usd = (n: number | null) => (n == null ? 'unknown' : `$${fmt(n, 0)}`);

export function Results({ state }: { state: ShareState }) {
  const { rooms, names, units, prefs } = state;
  const hasParticles = prefs.concerns.some((c) => c !== 'gases');
  return (
    <div className="space-y-8">
      {prefs.concerns.includes('gases') && <GasNotice prefs={prefs} mixed={hasParticles} />}
      {hasParticles && rooms.map((r, i) => (
        <RoomResult key={i} room={r} name={rooms.length > 1 ? names[i] || `Room ${i + 1}` : undefined} units={units} prefs={prefs} />
      ))}
      <p className="text-xs text-muted-foreground">
        Found a wrong number? <Link to="/corrections" className="text-primary underline">Report a correction</Link>. See <Link to="/methodology" className="text-primary underline">how this works</Link>.
      </p>
    </div>
  );
}

function RoomResult({ room, name, units, prefs }: { room: Room; name?: string; units: UnitSystem; prefs: Preferences }) {
  const res = matchRoom(room, prefs);
  const vol = units === 'metric' ? `${fmt(roomVolumeM3(room), 1)} m³` : `${fmt(roomVolumeFt3(room), 0)} ft³`;
  return (
    <Card className="cadr-result">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{name ? `${name}: ` : ''}{fmtArea(room, units)}, {fmtCeiling(room, units)} ceiling</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {res.target && res.required != null && (
          <div className="rounded-lg bg-muted p-4">
            <p className="text-sm text-muted-foreground">Clean air needed — {res.target.label}</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {units === 'metric' ? fmtRequiredM3h(requiredM3h(room, res.target.each)) : fmtRequiredCfm(res.required)}
              <span className="text-base font-normal text-muted-foreground ml-2">({units === 'metric' ? fmtRequiredCfm(res.required) : fmtRequiredM3h(toM3h(res.required))})</span>
            </p>
            <p className="text-sm text-muted-foreground mt-2 font-mono">
              {units === 'metric' ? `${vol} × ${res.target.each} = ${fmt(requiredM3h(room, res.target.each), 1)} m³/h` : `${vol} × ${res.target.each} ÷ 60 = ${fmt(res.required, 1)} CFM`}
            </p>
            <p className="text-sm text-muted-foreground mt-2">Checked against: {res.requiredMetrics.map((m) => METRIC_LABEL[m]).join(' and ')} — each must meet the target at the same speed.</p>
            <p className="text-xs text-muted-foreground mt-2">
              {res.target.basis}{' '}
              {res.target.sourceUrl && <a className="text-primary underline" href={res.target.sourceUrl} target="_blank" rel="noopener noreferrer">Source</a>}
            </p>
          </div>
        )}

        {res.notes.map((n) => <p key={n} className="flex gap-2 text-sm text-foreground"><Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />{n}</p>)}

        {res.qualifying.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-foreground">Setups that meet the target ({res.qualifying.length})</h3>
            <p className="text-xs text-muted-foreground">
              Sorted by {({ 'three-year': 'estimated 3-year cost', purchase: 'purchase price', noise: 'measured noise at the speed used', output: 'clean-air output' } as const)[prefs.sort]}; unknown values last. No overall score.
            </p>
            <div className="grid gap-3">
              {res.qualifying.map((s) => <SetupCard key={s.model.id + s.units} s={s} units={units} prefs={prefs} />)}
            </div>
          </div>
        )}

        {res.unknown.length > 0 && (
          <details className="rounded-lg border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium text-foreground">Not confirmed ({res.unknown.length}) — might fit, but the evidence cannot show it</summary>
            <ReasonList items={res.unknown} />
          </details>
        )}
        {res.excluded.length > 0 && (
          <details className="rounded-lg border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium text-foreground">Ruled out ({res.excluded.length})</summary>
            <ReasonList items={res.excluded} />
          </details>
        )}
        {prefs.country === 'US' && (
          <p className="text-xs text-muted-foreground">Held, discontinued and archived models never appear here. <Link to="/products" className="text-primary underline">See the full catalogue and its status</Link>.</p>
        )}
      </CardContent>
    </Card>
  );
}

function ReasonList({ items }: { items: { model: { id: string; brand: string; name: string }; reasons: string[] }[] }) {
  return (
    <ul className="mt-3 space-y-2 text-sm">
      {items.map((u) => (
        <li key={u.model.id}>
          <Link to={`/products/${u.model.id}`} className="font-medium text-primary underline">{u.model.brand} {u.model.name}</Link>
          <span className="text-muted-foreground"> — {u.reasons.join(' ')}</span>
        </li>
      ))}
    </ul>
  );
}

function SetupCard({ s, units, prefs }: { s: Setup; units: UnitSystem; prefs: Preferences }) {
  const offer = getOffer(s.model.id);
  const fs = getFilterSet(s.model.id);
  const fsSrc = getSource(fs?.sourceId);
  const output = units === 'metric' ? `${fmt(toM3h(cfm(s.totalCfm)), 0)} m³/h` : `${fmt(s.totalCfm, 0)} CFM`;
  const srcType = getSource(s.tests[0].sourceId)?.type ?? 'manufacturer';
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex gap-4">
        <div className="h-20 w-20 shrink-0 rounded-md bg-card flex items-center justify-center overflow-hidden border border-border">
          <img src={s.model.image} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/products/${s.model.id}`} className="font-semibold text-foreground hover:text-primary">{s.units > 1 ? '2 × ' : ''}{s.model.brand} {s.model.name}</Link>
            <Badge variant="outline">{SOURCE_TYPE_LABEL[srcType]}</Badge>
            {s.metricsUsed.includes('unspecified') && <Badge variant="secondary">Particle type not stated</Badge>}
          </div>
          <dl className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-sm">
            <div><dt className="text-xs text-muted-foreground">Output used</dt><dd className="text-foreground">{output}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Achieved</dt><dd className="text-foreground">{fmt(s.achievedEach, 1)} eACH</dd></div>
            <div><dt className="text-xs text-muted-foreground">Purchase</dt><dd className="text-foreground">{usd(s.costs.purchase)}</dd></div>
            <div><dt className="text-xs text-muted-foreground">3-year estimate</dt><dd className="text-foreground">{usd(s.costs.threeYear)}</dd></div>
          </dl>
        </div>
      </div>
      <details className="mt-3 text-sm">
        <summary className="cursor-pointer text-primary">Why it qualifies, costs and evidence</summary>
        <ul className="mt-2 space-y-1 text-muted-foreground">
          {s.tests.map((t) => {
            const src = getSource(t.sourceId);
            return (
              <li key={t.id}>
                {METRIC_LABEL[t.metric]}: {t.cfm} CFM per unit at {t.speed.toLowerCase()} speed ({t.filterConfig}). Source:{' '}
                {src && <a href={src.url} target="_blank" rel="noopener noreferrer nofollow" className="text-primary underline inline-flex items-center gap-1">{src.publisher} <ExternalLink className="h-3 w-3" /></a>}, read {src?.accessed}{src?.checked === 'supplied-audit-observation' ? ' (supplied audit observation)' : src?.checked === 'from-audit-notes' ? ' (audit notes)' : ''}.
              </li>
            );
          })}
          <li>Sound at that speed: {s.combinedNoiseDbA != null ? `${fmt(s.combinedNoiseDbA, 1)} dBA${s.units > 1 ? ' combined (two identical units ≈ +3 dB)' : ''}` : 'not on record'}.</li>
          <li>Electricity: {usd(s.costs.annualElectricity)}/yr at {prefs.costs.hoursPerDay} h/day and ${prefs.costs.tariffUsdPerKwh}/kWh{s.costs.electricityBasis === 'rated-max-power-upper-estimate' ? ' — rated maximum power, an upper estimate' : ''}{s.costs.electricityBasis == null ? ' (power not on record)' : ''}.</li>
          <li>Filters: {fs ? `${fs.retailName} (${fs.contents}), $${fs.priceUsd ?? '?'} each${fsSrc ? ` from ${fsSrc.publisher}, ${fs.availability.replace(/-/g, ' ')} on ${fs.observed}` : ''}.` : 'no record.'}</li>
          <li>Long-run filter cost: {s.costs.annualFilterMaintenance != null ? `${usd(s.costs.annualFilterMaintenance)}/yr` : 'unknown'}. First 36 months: {s.costs.filterBoxes36 != null ? `${s.costs.filterBoxes36} box${s.costs.filterBoxes36 === 1 ? '' : 'es'} = ${usd(s.costs.filterSpend36)} (filters supplied with the purifier are not charged again)` : 'unknown'}.</li>
          {s.costs.missing.length > 0 && <li>3-year total not shown because {s.costs.missing.join(', ')} is missing.</li>}
          {offer && <li>Price observed {offer.observed} at <a href={offer.url} className="text-primary underline" target="_blank" rel="noopener noreferrer nofollow">{offer.seller}</a>. Not an affiliate link. Prices change and are not tracked automatically.</li>}
          {s.caveats.map((c) => <li key={c} className="flex gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{c}</li>)}
        </ul>
      </details>
    </div>
  );
}

function GasNotice({ prefs, mixed }: { prefs: Preferences; mixed: boolean }) {
  const tips = gasGuidance(prefs.gasSource, prefs.gasPersistence);
  return (
    <Card>
      <CardContent className="p-5 space-y-2 text-sm">
        <h3 className="font-semibold text-foreground">Odours, gases and VOCs{prefs.gasSource ? ` — ${GAS_SOURCE_LABELS[prefs.gasSource].toLowerCase()}` : ''}</h3>
        {mixed && <p className="text-foreground">You chose particles and gases. The particle results below do <strong>not</strong> cover the gas side — treat them as two separate jobs.</p>}
        {tips.length > 0 ? <ul className="list-disc pl-5 space-y-1 text-muted-foreground">{tips.map((t) => <li key={t}>{t}</li>)}</ul>
          : <p className="text-muted-foreground">Tell us the likely source and how often it happens for more specific guidance.</p>}
        <p className="text-muted-foreground">Start with the source and ventilation. Sorbent media (such as activated carbon) helps for some chemicals, depending on the chemical, media type and amount, humidity and replacement schedule. ANSI/AHAM AC-4-2022 exists for chemical-gas reduction testing, but a result for one chemical does not prove performance for others. We hold no chemical-specific evidence for any model yet, so we make no gas recommendation.</p>
        <p className="text-muted-foreground">A purifier does not fix CO₂ build-up (ventilate), carbon monoxide (use a CO alarm and have appliances checked) or the moisture behind mould.</p>
        <p><Link to="/blog/odours-gases-and-vocs" className="text-primary underline">Read the gases guide</Link> · <a href="https://www.epa.gov/indoor-air-quality-iaq/guide-air-cleaners-home" target="_blank" rel="noopener noreferrer" className="text-primary underline">EPA guide to air cleaners</a></p>
      </CardContent>
    </Card>
  );
}
